export type Shade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
export const SHADES = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900,
] as const;

export type Family =
  | "lionYellow"
  | "lionBlue"
  | "gray"
  | "red"
  | "orange"
  | "yellow"
  | "green"
  | "teal"
  | "blue"
  | "darkBlue"
  | "blueHighlight"
  | "indigo"
  | "purple"
  | "pink";
export type AnyColor = Family | "white" | "black";

const BASE_COLORS: Record<Family, string> = {
  lionYellow: "#f4d03f",
  lionBlue: "#4b6fb3",
  gray: "#6b7280",
  red: "#ef4444",
  orange: "#f97316",
  yellow: "#ece812",
  green: "#22c55e",
  teal: "#14b8a6",
  blue: "#3b82f6",
  darkBlue: "#1e2a37",
  blueHighlight: "#679dbd",
  indigo: "#6366f1",
  purple: "#a855f7",
  pink: "#ec4899",
};

const SHADE_MULTIPLIERS: Record<Shade, number> = {
  50: 0.95, // very light
  100: 0.9, // light
  200: 0.8, // lighter
  300: 0.7, // light
  400: 0.6, // medium-light
  500: 0.5, // medium
  600: 1.0, // base color (unchanged)
  700: 0.4, // medium-dark
  800: 0.3, // dark
  900: 0.2, // very dark
};

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h: h * 360, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h / 360 + 1 / 3);
  const g = hue2rgb(p, q, h / 360);
  const b = hue2rgb(p, q, h / 360 - 1 / 3);

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function generateShades(baseColor: string): Record<Shade, string> {
  const { h, s, l } = hexToHsl(baseColor);
  const shades = {} as Record<Shade, string>;

  for (const shade of SHADES) {
    const multiplier = SHADE_MULTIPLIERS[shade];
    const newLightness = Math.max(0.05, Math.min(0.95, l * multiplier));
    shades[shade] = hslToHex(h, s, newLightness);
  }

  return shades;
}

export const brand = {
  white: "#ffffff",
  black: "#000000",
  ...Object.fromEntries(
    Object.entries(BASE_COLORS).map(([name, color]) => [
      name,
      generateShades(color),
    ])
  ),
} as Record<Family, Record<Shade, string>> & { white: string; black: string };

export function brandColor(
  name: AnyColor,
  shade: Shade = 600,
  alpha = 1
): string {
  if (name === "white")
    return alpha === 1 ? brand.white : `rgba(255, 255, 255, ${alpha})`;
  if (name === "black")
    return alpha === 1 ? brand.black : `rgba(0, 0, 0, ${alpha})`;

  const color = brand[name as Family][shade];
  if (alpha === 1) return color;

  // Convert hex to rgba for alpha
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
